import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {filterDescriptorsBySelection, getLeafAffectedObjectPathParts} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/descriptors"
import {
    getAccessoryTypeDescriptor,
    getPullerBaseIdFromPullerAccessoryType,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessories"
import {getHandleBaseId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/handles"
import {getDescriptorsOfType, isGroupPart, MaterialInfo} from "@cm/template-nodes"

/**
 * Returns the id (relative to baseId) of the single material input under baseId that the selected mesh part uses,
 * e.g. the slider or the tag of a puller. Undefined if the selection doesn't single one out.
 */
export function getSelectedMaterialSlot(baseId: string, context: ExtensionContext): string | undefined {
    const prefix = baseId + "/"
    const matches = getDescriptorsOfType(filterDescriptorsBySelection(context), MaterialInfo).filter(
        (d) => d.props.type === "input" && d.props.id.startsWith(prefix),
    )
    if (matches.length !== 1) return undefined
    return matches[0].props.id.slice(prefix.length)
}

function getSelectedPullerOrHandle(context: ExtensionContext): {type: "Puller" | "Handle"; baseId: string} | undefined {
    const accessoryTypeDescriptor = getAccessoryTypeDescriptor(filterDescriptorsBySelection(context))
    switch (accessoryTypeDescriptor?.props.value?.toLowerCase()) {
        case "puller":
            return {type: "Puller", baseId: getPullerBaseIdFromPullerAccessoryType(accessoryTypeDescriptor.props.id)}
        case "handle":
            return {type: "Handle", baseId: getHandleBaseId(accessoryTypeDescriptor.props.id)}
        default:
            return undefined
    }
}

/**
 * Moves the mesh part selection of the selected puller or handle to the mesh part(s) the given material input is applied to,
 * so the selection reflects the material slot being edited.
 */
export function selectMaterialInput(inputId: string, context: ExtensionContext): void {
    const selected = getSelectedPullerOrHandle(context)
    if (!selected) return

    // The editor shows the reference handle's inputs in "All" mode, so re-anchor the input on the selected handle.
    const ownBaseId = selected.type === "Handle" ? getHandleBaseId(inputId) : selected.baseId
    if (!inputId.startsWith(ownBaseId + "/")) return
    const targetId = selected.baseId + inputId.slice(ownBaseId.length)

    const {sceneManagerService} = context
    const descriptor = getDescriptorsOfType(sceneManagerService.$descriptorList(), MaterialInfo).find(
        (d) => d.props.type === "input" && d.props.id === targetId,
    )
    const affectedParts = descriptor?.props.affectedObjectPathParts
    if (!affectedParts) return

    // Only the mesh material groups: the affected parts also hold "root" parts of non-mesh nodes (the input, material assignments, ...).
    const meshParts = getLeafAffectedObjectPathParts(affectedParts).filter(({part}) => isGroupPart(part))
    if (meshParts.length === 0) return

    // Keep the procedural (non-group) parts, they identify the puller/handle itself.
    const proceduralParts = sceneManagerService.$selectedObjectPathParts().filter(({part}) => !isGroupPart(part))
    sceneManagerService.selectNodes([...proceduralParts, ...meshParts])
}
